import * as THREE from "three";
import { ChunkMesherImpl } from "../render/mesher";
import { Sky } from "../render/sky";
import type { AtlasBundle } from "../world/atlas";

/**
 * Renderer, scene and materials for the voxel world.
 *
 * Both terrain materials are `MeshBasicMaterial` with vertex colours: face
 * shading and ambient occlusion are baked into the geometry at mesh time, so
 * there are no lights to upload and the day/night cycle is just a change of
 * `material.color`.
 */
/**
 * Create the renderer, walking through progressively more conservative context
 * attribute sets. Browsers sometimes refuse a context outright — the GPU
 * process may be respawning, memory may be tight, or the driver may reject the
 * discrete-GPU request — and in those cases a plainer request usually succeeds
 * where the performance-minded one fails.
 */
function createRenderer(): THREE.WebGLRenderer {
  const attempts: THREE.WebGLRendererParameters[] = [
    { antialias: false, powerPreference: "high-performance", stencil: false },
    { antialias: false, stencil: false },
    {
      antialias: false,
      stencil: false,
      powerPreference: "low-power",
      failIfMajorPerformanceCaveat: false,
    },
  ];
  let lastError: unknown = null;
  for (const params of attempts) {
    try {
      return new THREE.WebGLRenderer(params);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Error creating WebGL context.");
}

export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly solidMaterial: THREE.MeshBasicMaterial;
  readonly waterMaterial: THREE.MeshBasicMaterial;
  readonly outlineMaterial: THREE.LineBasicMaterial;
  readonly sky = new Sky();
  /** The world atlas, also handed to the HUD for inventory icons. */
  readonly atlas: AtlasBundle;

  private readonly container: HTMLElement;
  /** Extra fog distance kept beyond the visible horizon. */
  fogFar = 8;

  private readonly onResize = (): void => this.resize();

  constructor(container: HTMLElement, atlas: AtlasBundle) {
    this.container = container;
    this.atlas = atlas;

    this.renderer = createRenderer();
    this.atlas.texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.autoClear = true;
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      1000,
    );

    this.scene.fog = new THREE.Fog(0xbfd8ee, 64, 96);
    this.scene.add(this.camera);
    this.scene.add(this.sky.mesh);

    this.solidMaterial = new THREE.MeshBasicMaterial({
      map: atlas.texture,
      vertexColors: true,
      alphaTest: 0.1,
      fog: true,
    });

    this.waterMaterial = new THREE.MeshBasicMaterial({
      map: atlas.texture,
      vertexColors: true,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: true,
    });
    // A flat translucent sheet keeps everything under it visible from every
    // angle, so a shoreline view cuts straight through the water into the sea
    // floor and cave walls — it reads as "the blocks are transparent". Like
    // Minecraft, water should close up as the view grazes the surface (you can
    // still see into water when looking down into it, but looking across it
    // turns opaque). The facing angle uses the quad's own normals, which the
    // mesher bakes into the geometry: screen-space derivatives of the view
    // position are numerically unstable near edge-on fragments, and that noise
    // made the water sparkle between opaque and translucent.
    this.waterMaterial.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <common>",
          "#include <common>\nvarying vec3 vWaterViewPosition;\nvarying vec3 vWaterNormal;",
        )
        .replace(
          "#include <project_vertex>",
          "#include <project_vertex>\nvWaterViewPosition = mvPosition.xyz;\nvWaterNormal = normalize(normalMatrix * normal);",
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          "#include <common>",
          "#include <common>\nvarying vec3 vWaterViewPosition;\nvarying vec3 vWaterNormal;",
        )
        .replace(
          "#include <color_fragment>",
          `#include <color_fragment>
          float waterFacing = abs(dot(normalize(vWaterNormal), normalize(vWaterViewPosition)));
          diffuseColor.a = mix(1.0, diffuseColor.a, smoothstep(0.22, 0.6, waterFacing));`,
        );
    };

    this.outlineMaterial = new THREE.LineBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.45,
      depthTest: true,
    });

    this.resize();
    window.addEventListener("resize", this.onResize);
  }

  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  createMesher(parent: THREE.Object3D): ChunkMesherImpl {
    return new ChunkMesherImpl(this.solidMaterial, this.waterMaterial, parent);
  }

  /** Fog range tracks the render distance so the world edge fades out. */
  setRenderDistance(chunks: number, chunkSize: number): void {
    const near = chunks * chunkSize * 0.55;
    const far = chunks * chunkSize + this.fogFar;
    const fog = this.scene.fog as THREE.Fog;
    fog.near = near;
    fog.far = far;
    // The sky dome sits inside the far plane, so keep the camera just inside it.
    this.camera.far = far * 2;
    this.camera.updateProjectionMatrix();
  }

  setFov(fov: number): void {
    if (this.camera.fov !== fov) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  }

  setGraphicsQuality(quality: "fast" | "fancy"): void {
    const ratio = quality === "fast" ? 1.0 : Math.min(window.devicePixelRatio, 2);
    if (this.renderer.getPixelRatio() !== ratio) {
      this.renderer.setPixelRatio(ratio);
      this.resize();
    }
  }

  resize(): void {
    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    window.removeEventListener("resize", this.onResize);
    this.sky.dispose();
    this.renderer.dispose();
    // `dispose()` frees three's caches but keeps the WebGL context alive until
    // GC. Browsers cap live contexts per page, so a few menu round-trips would
    // exhaust them and the next `new WebGLRenderer` would throw. Lose it now.
    this.renderer.forceContextLoss();
    this.renderer.domElement.remove();
  }
}
